import React, { useEffect, useState, createElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, GlobeIcon, EyeIcon, Edit2Icon, Trash2Icon, Loader2Icon, RefreshCwIcon, ExternalLinkIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { landingPagesApi, LandingPageRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function LandingPages() {
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [pages, setPages] = useState<LandingPageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewPage, setPreviewPage] = useState<LandingPageRecord | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showUrlId, setShowUrlId] = useState<string | null>(null);
  const fetchPages = async () => {
    setLoading(true);
    try {
      const data = await landingPagesApi.list();
      setPages(data);
    } catch (err) {
      console.error('Failed to fetch landing pages:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchPages();
  }, []);
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await landingPagesApi.delete(id);
      setPages(prev => prev.filter(p => p.ID !== id));
      setDeleteConfirm(null);
      toast.success('Landing page deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete landing page', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  const handleCopyUrl = (slug: string, id: string) => {
    const url = `${window.location.origin}/page/${slug}`;
    const tryClipboard = (): Promise<boolean> => {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(url).then(() => true).catch(() => false);
      }
      // execCommand fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        ta.remove();
        return Promise.resolve(ok);
      } catch {
        return Promise.resolve(false);
      }
    };
    tryClipboard().then(success => {
      if (success) {
        setCopiedId(id);
        toast.success('URL copied!', {
          description: url,
          duration: 3000
        });
        setTimeout(() => setCopiedId(null), 2000);
      } else {
        // Clipboard blocked (e.g. inside iframe) — show URL inline for manual copy
        setShowUrlId(prev => prev === id ? null : id);
      }
    });
  };
  const filteredPages = pages.filter(p => {
    if (p.Team !== currentWorkspace.name && p.Team !== team) return false;
    if (currentSubRegion) {
      return !p.Region || p.Region === currentSubRegion;
    }
    return true;
  });
  const statusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case 'draft':
        return 'bg-gray-50 text-gray-600 ring-gray-500/10';
      case 'archived':
        return 'bg-amber-50 text-amber-700 ring-amber-600/20';
      default:
        return 'bg-gray-50 text-gray-600 ring-gray-500/10';
    }
  };
  return <div className="space-y-6" data-id="element-4758" data-mp="35ujz9919jklh">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4759" data-mp="31r464moek2dj">
        <div className="min-w-0" data-id="element-4760" data-mp="3qhi4o0tsdppf">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4761" data-mp="3qiji2by9yoab">
            Landing Pages
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4762" data-mp="3vfjrmwuc2966">
            Build custom landing pages and link them to email templates as
            confirmation destinations.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-4763" data-mp="3lpqf9tmd9q0v">
          <button onClick={fetchPages} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-4764" data-mp="37ra861kldxi7">
            <RefreshCwIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4765" data-mp="2yrwvz1532uts" />
          </button>
          <Link to="/landing-pages/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-4766" data-mp="32tqvh63jlssd">
            <PlusIcon className="h-4 w-4" data-id="element-4767" data-mp="2libu434sbco4" />
            <span data-id="element-4768" data-mp="2ljelsd9xlj53">Create Page</span>
          </Link>
        </div>
      </div>

      {loading && pages.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4769" data-mp="2hiul4zq5fxe7">
          <Loader2Icon className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4770" data-mp="2v9zfbmaqzxh5" />
          <p className="mt-2 text-sm text-gray-500" data-id="element-4771" data-mp="2qgn2sajcfh4r">Loading landing pages...</p>
        </div> : filteredPages.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4772" data-mp="358p0pp1us025">
          <GlobeIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4773" data-mp="307807una6fm3" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4774" data-mp="3e7ip4s515k4m">
            No landing pages found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4775" data-mp="399h8seyznywu">
            Create your first landing page to use as a confirmation destination
            in email templates.
          </p>
          <Link to="/landing-pages/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4776" data-mp="1euy90k5u09ii">
            <PlusIcon className="h-4 w-4" data-id="element-4777" data-mp="3jwk1kgcluwy8" />
            Create First Page
          </Link>
        </div> : <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6" data-id="element-4778" data-mp="3ot0r9ezgdjdf">
          {filteredPages.map(page => <div key={page.ID} className="bg-white border border-gray-200 rounded-lg p-4 lg:p-6 flex flex-col" data-id="element-4779" data-mp="3stugcbomt47w">
              <div className="flex items-start justify-between mb-4 gap-3" data-id="element-4780" data-mp="6nxklptu8zaw">
                <div className="p-2 bg-indigo-50 rounded-lg flex-shrink-0" data-id="element-4781" data-mp="bf5uemz2ymol">
                  <GlobeIcon className="h-5 w-5 lg:h-6 lg:w-6 text-indigo-600" data-id="element-4782" data-mp="gesffil5h77y" />
                </div>
                <div className="flex items-center gap-2" data-id="element-4783" data-mp="kgdmgnb06ra8">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${statusColor(page.Status)}`} data-id="element-4784" data-mp="khyhee18cc8m">
                    {page.Status || 'Draft'}
                  </span>
                  {page.Team && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-4785" data-mp="24xmygmilsezh">
                      {page.Team}
                    </span>}
                </div>
              </div>
              <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-1 truncate" data-id="element-4786" data-mp="1w02pssievgeu">
                {page.Page_Name || 'Untitled Page'}
              </h3>
              {page.Url_Slug && <div className="mb-3" data-id="element-4787" data-mp="2ooro6js317mw">
                  <div className="flex items-center gap-1.5" data-id="element-4788" data-mp="2jundbpfp4fgj">
                    <p className="text-sm text-gray-500 truncate font-mono flex-1" data-id="element-4789" data-mp="2ewlth2ag4c40">
                      /page/{page.Url_Slug}
                    </p>
                    <button onClick={() => handleCopyUrl(page.Url_Slug, page.ID)} title="Copy confirmation URL" className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" data-id="element-4790" data-mp="3ccrmq5vmy2c1">
                      {copiedId === page.ID ? <CheckIcon className="h-3.5 w-3.5 text-green-500" data-id="element-4791" data-mp="2600tzeosbvgq" /> : <CopyIcon className="h-3.5 w-3.5" data-id="element-4792" data-mp="2ay2dg0q4hwsq" />}
                    </button>
                  </div>

                  {/* Inline URL panel — shown when clipboard is blocked */}
                  {showUrlId === page.ID && <div className="mt-2 p-2.5 bg-gray-50 border border-gray-200 rounded-md" data-id="element-4793" data-mp="33l6jm90ych63">
                      <p className="text-xs text-gray-500 mb-1.5 font-medium" data-id="element-4794" data-mp="2ppkp5mby956a">
                        Select all and copy (Ctrl+A / ⌘A then Ctrl+C / ⌘C):
                      </p>
                      <input type="text" readOnly value={`${window.location.origin}/page/${page.Url_Slug}`} onFocus={e => e.target.select()} onClick={e => (e.target as HTMLInputElement).select()} className="w-full text-xs font-mono bg-white border border-gray-300 rounded px-2 py-1.5 text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 cursor-text" data-id="element-4795" data-mp="1e6rqlciwi84s" />
                      <p className="text-xs text-gray-400 mt-1.5" data-id="element-4796" data-mp="rzxe3pdisa7t">
                        💡 Or just use the dropdown in the Email Template editor
                        — no copy needed.
                      </p>
                    </div>}
                </div>}

              {/* Confirmation URL hint */}
              {page.Status?.toLowerCase() === 'active' && page.Url_Slug && <div className="mb-4 bg-indigo-50 border border-indigo-100 rounded-md px-3 py-2" data-id="element-4797" data-mp="98wfj2gbagos">
                  <p className="text-xs text-indigo-700 font-medium mb-0.5" data-id="element-4798" data-mp="4auy0lyjapff">
                    Use as confirmation redirect
                  </p>
                  <p className="text-xs text-indigo-600" data-id="element-4799" data-mp="3s8u0lvq8qfl0">
                    Email Templates → open your template →{' '}
                    <strong data-id="element-4800" data-mp="3o6777qdx8mlp">"Redirect After Confirmation"</strong> → select this
                    page from the dropdown. No copy/paste needed.
                  </p>
                </div>}

              <div className="flex items-center gap-2 pt-4 border-t border-gray-100 mt-auto flex-wrap" data-id="element-4801" data-mp="39ar5r0b0hofa">
                <Link to={`/landing-pages/${page.ID}`} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 whitespace-nowrap" data-id="element-4802" data-mp="34k2glrmt13jb">
                  <Edit2Icon className="h-3 w-3" data-id="element-4803" data-mp="2vjw3i6v0q5ko" /> Edit
                </Link>
                <span className="text-gray-300" data-id="element-4804" data-mp="2qk9qnyj3qrce">|</span>
                <button onClick={() => setPreviewPage(page)} className="text-sm font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1 whitespace-nowrap" data-id="element-4805" data-mp="2qjro6k5ujgw1">
                  <EyeIcon className="h-3 w-3" data-id="element-4806" data-mp="2bjcdplgg3ljd" /> Preview
                </button>
                {page.Url_Slug && page.Status?.toLowerCase() === 'active' && <>
                    <span className="text-gray-300" data-id="element-4807" data-mp="2lg7a9yt5rbv0">|</span>
                    <a href={`/page/${page.Url_Slug}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1 whitespace-nowrap" data-id="element-4808" data-mp="2qesee9bj6foq">
                      <ExternalLinkIcon className="h-3 w-3" data-id="element-4809" data-mp="22o65kftpcrdo" /> View Live
                    </a>
                  </>}
                <span className="text-gray-300" data-id="element-4810" data-mp="27hr2s5nh1hbt">|</span>
                <button onClick={() => setDeleteConfirm(page.ID)} className="text-sm font-medium text-red-500 hover:text-red-700 flex items-center gap-1 whitespace-nowrap" data-id="element-4811" data-mp="19z7mnghr9l7b">
                  <Trash2Icon className="h-3 w-3" data-id="element-4812" data-mp="16wuqzhsaydq9" /> Delete
                </button>
              </div>
            </div>)}
        </div>}

      {/* Preview Modal */}
      <PreviewModal isOpen={!!previewPage} onClose={() => setPreviewPage(null)} title={`Preview: ${previewPage?.Page_Name}`} data-id="element-4813" data-mp="1gn5vb5fweoxo">
        {previewPage && <FormPreview html={previewPage.HTML || ''} css={previewPage.CSS} js={(previewPage as any).JavaScript1} data-id="element-4814" data-mp="o0uubtodqm0c" />}
      </PreviewModal>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4815" data-mp="xvmtv31ivixr">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4816" data-mp="sy3bps1pem0j">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4817" data-mp="29cd0gn9uycrw" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4818" data-mp="24g5yt6ohvnfr">
              <div className="flex items-center gap-4" data-id="element-4819" data-mp="2bzniql5nifcu">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4820" data-mp="2gwxdmgbt7j82">
                  <Trash2Icon className="h-6 w-6 text-red-600" data-id="element-4821" data-mp="22y7hp40q7pla" />
                </div>
                <div data-id="element-4822" data-mp="26zj12rt0bel1">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4823" data-mp="26yzrbydifwi3">
                    Delete Landing Page
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4824" data-mp="2uqeqji9zpxp4">
                    Are you sure? This will permanently remove the landing page.
                    Any email templates linking to this page will need to be
                    updated.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-4825" data-mp="3dn6fo8c7r713">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4826" data-mp="3dlmxudawsnx7">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-4827" data-mp="1psl293iua2n5">
                  {isDeleting && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-4828" data-mp="28gg8m2c4ge68" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}